import { useMemo, type ReactNode } from "react";

import { type ChartOptions } from "chart.js";
import clsx from "clsx";
import { Pie } from "react-chartjs-2";

import { GenericTooltip, Tooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";
import {
  type BudgetMixContributions,
  formatting,
  type FullBudgetMixResponse,
} from "@north-beam/nb-common";
import { type MetricPrecision } from "@north-beam/nb-common/formatting";
import { formatMetric } from "@north-beam/nb-common/formatting";

import { type Kpi, KpiType } from "../client-setting/types";

export type Props = {
  predictedValues: {
    low: number;
    median: number;
    high: number;
  };
  kpi: Kpi;
  budgetMixMetadata: FullBudgetMixResponse["create_budget_recommendations_metadata"];
};

export const PredictedRangeSection = ({
  predictedValues,
  kpi,
  budgetMixMetadata,
}: Props) => {
  const { user } = useUser();

  const formatCurrency = (value: number, precision: MetricPrecision) =>
    formatting.formatCurrency(value, {
      precision,
      code: user.currencies.currentCurrency,
    });

  const formatNonCurrency = (value: number, precision: MetricPrecision) =>
    formatting.formatMetric(value, "decimal", { precision });

  const isValueTypeRevenue = kpi.kpi_type === KpiType.REVENUE;

  const contributions: BudgetMixContributions | null = useMemo(() => {
    return budgetMixMetadata?.contribution_data?.[kpi.kpi_name] ?? null;
  }, [budgetMixMetadata, kpi.kpi_name]);

  const formatValue = (
    value: number,
    precision: MetricPrecision = "approximate",
  ) =>
    isValueTypeRevenue
      ? formatCurrency(value, precision)
      : formatNonCurrency(value, precision);

  return (
    <div className="h-full w-full p-3 flex flex-col items-start flex-[1_0_0]">
      <div className="flex justify-start items-center w-full gap-2">
        <Tooltip
          placement="top"
          content={kpi.friendly_name}
          noInfoCircle
          showTooltip={kpi.friendly_name.length > 22}
          className="text-xs font-semibold uppercase mb-1 whitespace-nowrap overflow-hidden overflow-ellipsis max-w-[180px]"
        >
          {kpi.friendly_name}
        </Tooltip>
        <div className="flex justify-start items-center gap-1 mb-1">
          <i className="fa-regular fa-bullseye-arrow text-cosmic text-xxs" />
          <span className="text-xxs text-gray+1">Forecasted</span>
        </div>
      </div>
      <div className="flex self-stretch gap-1 items-center h-5">
        <PredictedRangeTooltip
          predictedValues={predictedValues}
          formatValue={formatValue}
          className="text-xs font-normal"
          contributions={contributions}
        >
          {formatValue(predictedValues.low)}
        </PredictedRangeTooltip>
        <div
          className={clsx(
            "bg-gradient-to-r from-mars to-sun min-w-[34px]",
            "flex-[1_0_0] h-[3px] rounded-sm",
          )}
        />
        <PredictedRangeTooltip
          predictedValues={predictedValues}
          formatValue={formatValue}
          className="text-cosmic text-lg font-semibold"
          contributions={contributions}
        >
          {formatValue(predictedValues.median)}
        </PredictedRangeTooltip>
        <div
          className={clsx(
            "bg-gradient-to-r from-sun to-alien min-w-[34px]",
            "flex-[1_0_0] h-[3px] rounded-sm",
          )}
        />
        <PredictedRangeTooltip
          predictedValues={predictedValues}
          formatValue={formatValue}
          className="text-xs font-normal"
          contributions={contributions}
        >
          {formatValue(predictedValues.high)}
        </PredictedRangeTooltip>
      </div>
      <div className="flex justify-center items-center gap-1 self-stretch mt-[2px]">
        <span className="text-2xs text-gray+1">Median</span>
      </div>
    </div>
  );
};

const CONTRIBUTION_LABELS = {
  contribution_spend: "Media Impact (spend)",
  contribution_baseline: "Baseline Impact",
  contribution_promotion: "Promotion Impact",
  contribution_spend_times_promo: "Additional Media Impact due to Promotions",
};
const PredictedRangeTooltip = ({
  predictedValues,
  children,
  formatValue,
  className,
  contributions,
}: {
  predictedValues: Props["predictedValues"];
  children: ReactNode;
  formatValue: (value: number, precision?: MetricPrecision) => string;
  className?: string;
  contributions: BudgetMixContributions | null;
}) => {
  const { designElementManager } = useDesignElementManagerV2();
  const summation = useMemo(() => {
    return Object.values(contributions ?? {}).reduce(
      (acc, val) => acc + val,
      0,
    );
  }, [contributions]);

  return (
    <Tooltip
      noInfoCircle
      className={className}
      content={
        <div>
          <div className="text-base text-center mb-1">Predicted range</div>
          <div className="flex self-stretch gap-2 items-center h-8 pb-3">
            <div className="text-xs font-normal relative">
              {formatValue(predictedValues.low, "exact")}
              <div className="absolute-horizontal-center -bottom-4 text-2xs text-gray+1">
                Low
              </div>
            </div>
            <div
              className={clsx(
                "bg-gradient-to-r from-mars to-sun min-w-[16px]",
                "flex-[1_0_0] h-[3px] rounded-sm",
              )}
            />
            <div className="text-cosmic text-lg font-semibold relative">
              {formatValue(predictedValues.median, "exact")}
              <div className="font-normal absolute-horizontal-center -bottom-3 text-2xs text-gray+1">
                Median
              </div>
            </div>
            <div
              className={clsx(
                "bg-gradient-to-r from-sun to-alien min-w-[16px]",
                "flex-[1_0_0] h-[3px] rounded-sm",
              )}
            />
            <div className="text-xs font-normal relative">
              {formatValue(predictedValues.high, "exact")}
              <div className="absolute-horizontal-center -bottom-4 text-2xs text-gray+1">
                High
              </div>
            </div>
          </div>
          {contributions && (
            <div className="w-full">
              <div className="text-base text-center mb-1 mt-5">
                Impact by source
              </div>
              <ContributionsPieChart
                contributions={contributions}
                formatValue={formatValue}
              />
              <div className="flex flex-col  gap-2  w-[15rem] mx-auto  pb-3 mt-5">
                {Object.keys(contributions ?? {}).map((contribution) => (
                  <div
                    key={contribution}
                    className="text-xs font-normal relative   gap-2 grid grid-cols-6"
                  >
                    <div className="flex gap-2 col-span-4">
                      <div
                        className="min-w-[1rem] min-h-[1rem] max-h-[1rem] rounded-sm"
                        style={{
                          backgroundColor:
                            designElementManager.getDesignElementColor(
                              contribution,
                            ),
                        }}
                      />
                      <div className="text-2xs text-gray+1">
                        {
                          CONTRIBUTION_LABELS[
                            contribution as keyof BudgetMixContributions
                          ]
                        }
                      </div>
                    </div>
                    <div className="col-span-2 flex gap-1">
                      <div>
                        {formatValue(
                          contributions?.[
                            contribution as keyof BudgetMixContributions
                          ] ?? 0,
                          "approximate",
                        )}
                      </div>
                      <div>
                        (
                        {formatting.formatPercentage(
                          (contributions?.[
                            contribution as keyof BudgetMixContributions
                          ] ?? 0) / summation,
                          { precision: "approximate" },
                        )}
                        )
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};

export const ContributionsPieChart = ({
  contributions,
  formatValue,
}: {
  contributions: BudgetMixContributions;
  formatValue: (value: number, format: "exact" | "approximate") => string;
}) => {
  const { designElementManager } = useDesignElementManagerV2();

  const data = useMemo(
    () => ({
      labels: Object.keys(contributions),
      datasets: [
        {
          data: Object.values(contributions),
          backgroundColor: Object.keys(contributions).map((label) =>
            designElementManager.getDesignElementColor(label),
          ),
          borderWidth: 1,
        },
      ],
    }),
    [designElementManager, contributions],
  );

  const options = useMemo<ChartOptions<"pie">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "0%",
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.label || "";
              const value = context.parsed;

              return `${
                CONTRIBUTION_LABELS[label as keyof BudgetMixContributions]
              }: ${formatValue(value, "approximate")}`;
            },
          },
        },
      },
    }),
    [formatValue],
  );

  return (
    <div className="mx-auto pt-2">
      <Pie data={data} options={options} />
    </div>
  );
};
